import React from 'react';
import { useSearchParams } from 'next/navigation';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { isSponsoredArticle } from '@benzinga/article-manager/sponsored-utils';
import { isPaywallArticle, isFullHeadline } from '@benzinga/article-manager';
import { truncate } from '@benzinga/utils/stringUtils';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { NoFirstRender } from '@benzinga/hooks';
import { useIsUserTrialing, usePermission } from '@benzinga/user-context';
import { EdgeBadge, ProBadge } from '@benzinga/core-ui';
import type { News } from '@benzinga/basic-news-manager';

const TIMEZONE = 'America/New_York';

const DateOrTimeAgo = ({ dateTimeString = '' }: { dateTimeString: string }): React.ReactElement | null => {
  const { i18n } = useTranslation();
  const dateTimeLuxon = DateTime.fromJSDate(new Date(dateTimeString), { locale: i18n.language }).setZone(TIMEZONE);
  if (!dateTimeLuxon.isValid) {
    return null;
  }
  const dateTimeFormatted = dateTimeLuxon
    .toFormat('ff', { locale: i18n.language })
    ?.replace(' AM', 'AM')
    ?.replace(' PM', 'PM');
  const dateTime =
    dateTimeLuxon > DateTime.local().minus({ days: 7 })
      ? dateTimeLuxon.toRelative({ locale: i18n.language })
      : dateTimeFormatted;
  return (
    <>
      <span> - </span>
      <span className="text-gray-500 content-headline-datetime">{dateTime}</span>
    </>
  );
};

interface Props {
  content: News;
  isProOnly?: boolean;
  impression?: boolean;
}

const ContentHeadline = React.memo((props: Props) => {
  const { t } = useTranslation('quote');
  const { content, impression, isProOnly } = props;
  const searchParams = useSearchParams();
  const utmSource = searchParams?.get('utm_source') ?? null;
  const hasProDashboard = usePermission('bzpro/dashboard/open', '#');
  const isProTrialing = useIsUserTrialing('benzinga-pro');
  const isBenzingaProUser = hasProDashboard || isProTrialing;

  if (!content) {
    return null;
  }

  const isSponsored = isSponsoredArticle(content);

  const href = content.url ? content.url : `/article/${content.id}`;

  const hasBadges = isProOnly || isPaywallArticle(content);

  const showFullHeadline = isFullHeadline(content.author, utmSource) || !isProOnly || isBenzingaProUser;
  const displayTitle = showFullHeadline ? (content.title ?? '') : truncate(content.title ?? '', 45);
  const disabledLink = !!isProOnly;

  return (
    <a
      className={`py-2 block content-headline ${disabledLink ? 'cursor-default' : ''}`}
      data-impression={impression && content.id}
      href={disabledLink ? '#' : href}
      key={content.id}
      rel="noopener noreferrer"
      target={disabledLink ? '_self' : '_blank'}
    >
      <div className="content-title title-text text-base font-semibold line-clamp-3 md:line-clamp-2 text-gray-900 hover:text-blue-600">
        {hasBadges && (
          <span className="inline-block align-middle mr-2 flex-shrink-0 scale-90">
            {isProOnly ? <ProBadge /> : EdgeBadge}
          </span>
        )}
        <span
          className="inline"
          dangerouslySetInnerHTML={{
            __html: sanitizeHTML(displayTitle),
          }}
        />
      </div>
      <div className="text-gray-400 text-sm author-date-text">
        {content.author}
        {isSponsored ? (
          <>
            <span> - </span>
            <span className="news-item-sponsored-tag font-semibold text-blue-500 text-sm">
              {t('Sponsored.sponsored')}
            </span>
          </>
        ) : (
          <NoFirstRender>
            <DateOrTimeAgo dateTimeString={content.created} />
          </NoFirstRender>
        )}
      </div>
    </a>
  );
});

export default ContentHeadline;
